import { z } from 'zod';

const timerLockSchema = z.object({
    ownerTabId: z.string(),
    endsAt: z.number(),
});

type TimerLock = z.infer<typeof timerLockSchema>;
type Subscriber = (active: boolean) => void;

const STORAGE_KEY = 'nesso:active-recording';
const HEARTBEAT_INTERVAL_MS = 5000;
const STALE_AFTER_MS = 15000;
const TAB_ID = crypto.randomUUID();
const subscribers = new Set<Subscriber>();

let lifecycle: AbortController | null = null;
let lastActiveRecording: boolean | null = null;

function readLock(): TimerLock | null {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const result = timerLockSchema.safeParse(JSON.parse(raw));
        return result.success ? result.data : null;
    } catch {
        console.warn("[Cross Tab] can't read item from localStorage");
        return null;
    }
}

function writeLock(lock: TimerLock): void {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lock));
    } catch {
        console.warn("[Cross Tab] can't write item to localStorage");
    }
}

function removeLock(): void {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch {
        console.warn("[Cross Tab] can't remove item from localStorage");
    }
}

function notifyAll(): void {
    const active = isRecordingActiveAnywhere();
    if (active === lastActiveRecording) return;
    lastActiveRecording = active;
    subscribers.forEach((subscriber) => subscriber(active));
}

function wireUp(): void {
    if (typeof window === 'undefined' || lifecycle !== null) return;
    lifecycle = new AbortController();
    const { signal } = lifecycle;

    window.addEventListener(
        'storage',
        (event) => {
            if (event.key === STORAGE_KEY || event.key === null) {
                notifyAll();
            }
        },
        { signal },
    );

    const intervalId = setInterval(notifyAll, HEARTBEAT_INTERVAL_MS);
    signal.addEventListener('abort', () => clearInterval(intervalId));
}

function tearDown(): void {
    lifecycle?.abort();
    lifecycle = null;
}

export function isRecordingActiveAnywhere(): boolean {
    const lock = readLock();
    if (!lock) return false;
    return lock.endsAt > Date.now();
}

export function publishRecordingActive(): () => void {
    const controller = new AbortController();
    const { signal } = controller;

    const write = () => {
        writeLock({ ownerTabId: TAB_ID, endsAt: Date.now() + STALE_AFTER_MS });
        notifyAll();
    };

    write();
    const intervalId = setInterval(write, HEARTBEAT_INTERVAL_MS);

    signal.addEventListener('abort', () => {
        clearInterval(intervalId);
        const lock = readLock();
        if (lock?.ownerTabId === TAB_ID) {
            removeLock();
        }
        notifyAll();
    });

    if (typeof window !== 'undefined') {
        window.addEventListener('pagehide', () => controller.abort(), { signal });
    }

    return () => controller.abort();
}

export function subscribeToRecordingActive(callback: Subscriber): () => void {
    wireUp();
    subscribers.add(callback);
    callback(isRecordingActiveAnywhere());

    return () => {
        subscribers.delete(callback);
        if (subscribers.size === 0) {
            tearDown();
        }
    };
}
